import { gsap } from 'gsap';
import type { PresentationScene } from './types';
import { InputController } from './InputController';
import { prefersReducedMotion } from './BaseScene';
import { ThreeStage } from '../three/ThreeStage';
import { ProgressMap } from '../ui/ProgressMap';
import type { ResolvedScene } from '../presentation/types';

/** Logical artboard every scene is authored on (02-DESKTOP-CANVAS-AND-LAYOUT.md). */
const ARTBOARD = { width: 1600, height: 900 };
/** Smallest desktop window the showcase is art-directed for; below it a notice is shown. */
export const MIN_VIEWPORT = { width: 1180, height: 660 };

export class PresentationController {
  private readonly shell: HTMLDivElement;
  private readonly sceneHost: HTMLDivElement;
  private readonly stage: ThreeStage;
  private readonly scenes: PresentationScene[];
  private readonly progress: ProgressMap;
  private readonly input: InputController;
  private readonly counter: HTMLElement;
  private readonly chapter: HTMLElement;
  private readonly timing: HTMLElement;
  private readonly notes: HTMLElement;
  private readonly notesTitle: HTMLElement;
  private readonly notesText: HTMLElement;
  private readonly stepText: HTMLElement;
  private readonly controls: HTMLElement;
  private readonly topHud: HTMLElement;
  private readonly bottomHud: HTMLElement;
  private readonly help: HTMLElement;
  private readonly handoff: HTMLElement;
  private readonly live: HTMLElement;
  private readonly resizeObserver: ResizeObserver;

  private sceneIndex = 0;
  private notesOpen = false;
  private helpOpen = false;
  private hudHidden = true;
  private idleTimer = 0;

  /** `sequence` comes from presentation/registry.ts: only enabled modules, already in running order. */
  constructor(private readonly root: HTMLElement, sequence: ResolvedScene[]) {
    this.root.innerHTML = `
      <main class="presentation-shell" data-controls-idle="false">
        <div class="stage-backdrop" aria-hidden="true"></div>
        <div class="ambient-wash"></div>
        <div class="noise-overlay"></div>
        <div class="presentation-surface">
          <div class="scene-host"></div>
          <div class="brand-handoff" aria-hidden="true"></div>

          <header class="top-hud">
            <div class="brand-lockup">
              <img class="brand-lockup__logo brand-lockup__logo--dark" src="/branding/moderta/moderta-logo-white.png" alt="Moderta" width="76" height="22">
              <img class="brand-lockup__logo brand-lockup__logo--light" src="/branding/moderta/moderta-logo-black.png" alt="" aria-hidden="true" width="76" height="22">
              <span class="brand-lockup__divider" aria-hidden="true"></span>
              <span class="brand-lockup__chapter"></span>
            </div>
            <div class="scene-meta"><span class="scene-counter"></span><span class="scene-time"></span></div>
          </header>

          <aside class="presenter-notes" aria-hidden="true" data-open="false">
            <small>PRESENTER · N to toggle</small>
            <b class="presenter-notes__title"></b>
            <p></p>
          </aside>

          <div class="keyboard-help" role="dialog" aria-modal="false" aria-label="Keyboard controls" aria-hidden="true" data-open="false">
            <h2>Controls</h2>
            <dl>
              <dt><kbd>→</kbd> <kbd>Space</kbd> <kbd>Enter</kbd> or click</dt><dd>Next step</dd>
              <dt><kbd>←</kbd> <kbd>Backspace</kbd></dt><dd>Previous step</dd>
              <dt><kbd>Home</kbd> <kbd>End</kbd></dt><dd>First / last scene</dd>
              <dt><kbd>1</kbd>–<kbd>9</kbd> <kbd>0</kbd></dt><dd>Jump to scenes 1–10</dd>
              <dt><kbd>N</kbd></dt><dd>Presenter cues</dd>
              <dt><kbd>H</kbd></dt><dd>Show or hide navigation</dd>
              <dt><kbd>F</kbd></dt><dd>Fullscreen</dd>
              <dt><kbd>R</kbd></dt><dd>Restart</dd>
              <dt><kbd>?</kbd> <kbd>Esc</kbd></dt><dd>Toggle / close this panel</dd>
            </dl>
            <p>The showcase never advances on its own.</p>
          </div>

          <div class="step-indicator"></div>

          <footer class="bottom-hud">
            <div class="progress-slot"></div>
            <div class="control-cluster">
              <button class="icon-button" type="button" data-action="prev" aria-label="Previous step">←</button>
              <button class="icon-button icon-button--primary" type="button" data-action="next" aria-label="Next step">→</button>
              <button class="text-button" type="button" data-action="notes" aria-label="Toggle presenter cues">N · Notes</button>
              <button class="text-button" type="button" data-action="hud" aria-label="Hide navigation">H · Hide</button>
              <button class="text-button" type="button" data-action="fullscreen" aria-label="Toggle fullscreen">F · Fullscreen</button>
              <button class="text-button" type="button" data-action="help" aria-label="Keyboard controls">?</button>
            </div>
          </footer>
          <p class="sr-live" aria-live="polite"></p>
        </div>
        <div class="viewport-notice" role="status">
          <img src="/branding/moderta/moderta-logo-white.png" alt="Moderta" width="110" height="32">
          <p>This showcase is designed for desktop displays.</p>
          <p class="viewport-notice__detail">Enlarge the window to at least ${MIN_VIEWPORT.width} × ${MIN_VIEWPORT.height} pixels.</p>
        </div>
      </main>
    `;

    this.shell = this.root.querySelector<HTMLDivElement>('.presentation-shell')!;
    this.sceneHost = this.root.querySelector<HTMLDivElement>('.scene-host')!;
    this.counter = this.root.querySelector<HTMLElement>('.scene-counter')!;
    this.chapter = this.root.querySelector<HTMLElement>('.brand-lockup__chapter')!;
    this.timing = this.root.querySelector<HTMLElement>('.scene-time')!;
    this.notes = this.root.querySelector<HTMLElement>('.presenter-notes')!;
    this.notesTitle = this.root.querySelector<HTMLElement>('.presenter-notes__title')!;
    this.notesText = this.root.querySelector<HTMLElement>('.presenter-notes p')!;
    this.stepText = this.root.querySelector<HTMLElement>('.step-indicator')!;
    this.controls = this.root.querySelector<HTMLElement>('.control-cluster')!;
    this.topHud = this.root.querySelector<HTMLElement>('.top-hud')!;
    this.bottomHud = this.root.querySelector<HTMLElement>('.bottom-hud')!;
    this.help = this.root.querySelector<HTMLElement>('.keyboard-help')!;
    this.handoff = this.root.querySelector<HTMLElement>('.brand-handoff')!;
    this.live = this.root.querySelector<HTMLElement>('.sr-live')!;

    this.stage = new ThreeStage(this.shell);
    this.scenes = sequence.map((resolved) => {
      this.stage.registerAtmosphere(resolved.meta.id, resolved.definition.atmosphere);
      return resolved.definition.create(resolved);
    });

    this.scenes.forEach((scene) => scene.mount(this.sceneHost));
    this.progress = new ProgressMap(this.scenes.map((scene) => scene.meta), (index) => this.jumpTo(index));
    this.root.querySelector('.progress-slot')?.appendChild(this.progress.element);

    this.input = new InputController(this.root.querySelector('.presentation-surface')!, {
      next: () => this.next(),
      previous: () => this.previous(),
      toggleNotes: () => this.toggleNotes(),
      toggleFullscreen: () => this.toggleFullscreen(),
      toggleHud: () => this.toggleHud(),
      toggleHelp: () => this.toggleHelp(),
      dismiss: () => this.dismiss(),
      restart: () => this.restart(),
      jumpTo: (index) => this.jumpTo(index),
      first: () => this.jumpTo(0),
      last: () => this.jumpTo(this.scenes.length - 1),
      wakeControls: () => this.wakeControls(),
    });

    this.controls.addEventListener('click', this.onControlClick);
    this.resizeObserver = new ResizeObserver(() => this.fitArtboard());
    this.resizeObserver.observe(this.sceneHost);

    // Development only: lets QA scripts and rehearsal jump straight to any scene and step.
    if (import.meta.env.DEV) {
      (window as unknown as { __presentation: unknown }).__presentation = {
        goTo: (index: number, step = 0) => this.switchScene(index, step),
        scenes: () => this.scenes.map((scene) => ({ id: scene.meta.id, steps: scene.stepCount })),
      };
    }
  }

  public start(): void {
    this.input.bind();
    this.scenes.forEach((scene) => scene.exit(this.stage));
    this.sceneIndex = 0;
    this.scenes[0].goToStep(0, this.stage, true);
    this.scenes[0].enter(this.stage, true);
    this.updateHud();
    this.fitArtboard();
    this.applyHudVisibility(true);
    this.wakeControls();
    this.prewarmScenes();
  }

  /**
   * Scenes author HTML on a 1600×900 artboard (as SVG diagrams already do with their viewBox).
   * One uniform scale keeps every alignment anchor identical across desktop sizes.
   */
  private fitArtboard(): void {
    const layout = this.scenes[this.sceneIndex]?.root.querySelector('.scene-layout');
    const rect = (layout ?? this.sceneHost).getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const scale = Math.min(rect.width / ARTBOARD.width, rect.height / ARTBOARD.height);
    this.shell.style.setProperty('--artboard-scale', scale.toFixed(4));
  }

  /**
   * The first time a scene becomes visible the browser has to style and rasterise it from
   * scratch, which can stall a transition for a noticeable moment. While the opening plays, each
   * hidden scene is made paintable (but invisible) for a couple of frames, one at a time.
   */
  private prewarmScenes(): void {
    const queue = this.scenes.filter((_, index) => index !== this.sceneIndex);
    const idle = (callback: () => void): void => {
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(callback, { timeout: 1500 });
      else setTimeout(callback, 200);
    };
    const next = (): void => {
      const scene = queue.shift();
      if (!scene) return;
      if (scene === this.scenes[this.sceneIndex]) { idle(next); return; }
      scene.root.classList.add('is-warming');
      requestAnimationFrame(() => requestAnimationFrame(() => {
        scene.root.classList.remove('is-warming');
        idle(next);
      }));
    };
    window.setTimeout(() => idle(next), 1200);
  }

  private next(): void {
    const scene = this.scenes[this.sceneIndex];
    if (scene.next(this.stage)) {
      this.updateHud();
      return;
    }
    if (this.sceneIndex >= this.scenes.length - 1) {
      this.flashEnd();
      return;
    }
    this.switchScene(this.sceneIndex + 1, 0);
  }

  private previous(): void {
    const scene = this.scenes[this.sceneIndex];
    if (scene.previous(this.stage)) {
      this.updateHud();
      return;
    }
    if (this.sceneIndex <= 0) return;
    const previousScene = this.scenes[this.sceneIndex - 1];
    this.switchScene(this.sceneIndex - 1, previousScene.stepCount - 1);
  }

  private switchScene(index: number, step: number): void {
    const current = this.scenes[this.sceneIndex];
    current.exit(this.stage);

    this.sceneIndex = Math.max(0, Math.min(index, this.scenes.length - 1));
    const next = this.scenes[this.sceneIndex];
    const brandChanged = current.meta.brand !== next.meta.brand;
    next.goToStep(step, this.stage, true);
    next.enter(this.stage);
    this.updateHud();
    this.fitArtboard();
    this.playSceneTransition(brandChanged, index >= this.scenes.indexOf(current));
  }

  private jumpTo(index: number): void {
    if (index < 0 || index >= this.scenes.length) return;
    if (index === this.sceneIndex) {
      this.scenes[index].goToStep(0, this.stage, true);
      this.updateHud();
      return;
    }
    this.switchScene(index, 0);
  }

  private restart(): void {
    this.switchScene(0, 0);
  }

  private updateHud(): void {
    const scene = this.scenes[this.sceneIndex];
    const pad = (value: number): string => String(value).padStart(2, '0');
    const notes = scene.script.presenterNotes;
    this.counter.textContent = `${pad(this.sceneIndex + 1)} / ${pad(this.scenes.length)}`;
    this.chapter.textContent = scene.meta.chapter;
    this.timing.textContent = scene.meta.duration;
    this.notesTitle.textContent = `${scene.script.title} · ${scene.step + 1}/${scene.stepCount}`;
    this.notesText.textContent = notes[Math.min(scene.step, notes.length - 1)] ?? '';
    this.stepText.textContent = `STEP ${scene.step + 1} / ${scene.stepCount}`;
    this.progress.update(this.sceneIndex, scene.step, scene.stepCount);
    this.shell.dataset.cinematic = String(scene.meta.cinematic === true);
    // The shell takes the active brand's stage tokens; chrome stays neutral apart from the active marker.
    this.shell.dataset.brand = scene.meta.brand;
    this.shell.dataset.tone = scene.meta.tone;
    this.stage.element.dataset.tone = scene.meta.tone;
    document.documentElement.style.setProperty('--accent', scene.meta.accent);
    this.stage.setAccent(scene.meta.accent);
    this.live.textContent = `${scene.meta.title}, step ${scene.step + 1} of ${scene.stepCount}`;
  }

  private toggleNotes(): void {
    this.notesOpen = !this.notesOpen;
    this.notes.dataset.open = String(this.notesOpen);
    this.notes.setAttribute('aria-hidden', String(!this.notesOpen));
  }

  private toggleHelp(): void {
    this.helpOpen = !this.helpOpen;
    this.help.dataset.open = String(this.helpOpen);
    this.help.setAttribute('aria-hidden', String(!this.helpOpen));
  }

  private dismiss(): void {
    if (this.helpOpen) this.toggleHelp();
    else if (this.notesOpen) this.toggleNotes();
  }

  private toggleHud(): void {
    this.hudHidden = !this.hudHidden;
    this.applyHudVisibility();
  }

  private applyHudVisibility(immediate = false): void {
    const opacity = this.hudHidden ? 0 : 1;
    gsap.to([this.topHud, this.bottomHud, this.stepText], {
      opacity,
      duration: immediate ? 0 : 0.28,
      ease: 'power2.out',
      overwrite: true,
    });
    this.shell.dataset.hud = this.hudHidden ? 'hidden' : 'visible';
    this.topHud.style.pointerEvents = this.hudHidden ? 'none' : '';
    this.bottomHud.style.pointerEvents = this.hudHidden ? 'none' : '';
  }

  private async toggleFullscreen(): Promise<void> {
    try {
      if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
      else await document.exitFullscreen();
    } catch {
      // Presentation remains usable if fullscreen is denied.
    }
  }

  private wakeControls(): void {
    this.shell.dataset.controlsIdle = 'false';
    window.clearTimeout(this.idleTimer);
    this.idleTimer = window.setTimeout(() => {
      this.shell.dataset.controlsIdle = 'true';
    }, 2600);
  }

  private onControlClick = (event: MouseEvent): void => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>('button[data-action]');
    if (!button) return;
    const action = button.dataset.action;
    if (action === 'next') this.next();
    if (action === 'prev') this.previous();
    if (action === 'notes') this.toggleNotes();
    if (action === 'hud') this.toggleHud();
    if (action === 'fullscreen') this.toggleFullscreen();
    if (action === 'help') this.toggleHelp();
  };

  /**
   * ProductTransition. Within a brand, scenes crossfade with a slight settle. Across brands, a
   * curtain in the incoming brand's stage colour wipes in the direction of travel while the stage
   * backdrop morphs underneath, so no brand colour ever bleeds into another brand's scene.
   */
  private playSceneTransition(brandChanged: boolean, forward: boolean): void {
    if (prefersReducedMotion()) return;
    gsap.fromTo(this.sceneHost, { scale: 0.992 }, { scale: 1, duration: 0.6, ease: 'power2.out' });
    if (!brandChanged) return;
    const scene = this.scenes[this.sceneIndex];
    this.handoff.dataset.brand = scene.meta.brand;
    const from = forward ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)';
    const to = forward ? 'inset(0 100% 0 0)' : 'inset(0 0 0 100%)';
    gsap.killTweensOf(this.handoff);
    gsap.timeline()
      .set(this.handoff, { autoAlpha: 1, clipPath: from })
      .to(this.handoff, { clipPath: 'inset(0 0% 0 0%)', duration: 0.42, ease: 'power3.in' })
      .to(this.handoff, { clipPath: to, duration: 0.55, ease: 'power3.out' })
      .set(this.handoff, { autoAlpha: 0 });
    gsap.fromTo(scene.root, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5, delay: 0.38, ease: 'power2.out', overwrite: true });
  }

  private flashEnd(): void {
    const target = this.scenes[this.sceneIndex].root.querySelector('[data-end-anchor]');
    if (!target) return;
    gsap.fromTo(target, { scale: 1 }, { scale: 1.02, duration: 0.22, yoyo: true, repeat: 1, ease: 'power2.inOut' });
  }
}
